// Platform (control-plane) tenant registry — mirrors PlatformTenantDto.
// Secrets (ConnectionString) are never sent by the backend.

export type TenantStatus = 'Pending' | 'Provisioning' | 'Active' | 'Suspended' | 'Cancelled';

export const TENANT_STATUSES: TenantStatus[] = ['Pending', 'Provisioning', 'Active', 'Suspended', 'Cancelled'];

export interface PlatformTenant {
  id: number;
  code: string;
  companyName: string;
  legalName?: string | null;
  email: string;
  phone?: string | null;
  subdomain: string;
  status: TenantStatus;
  isActive: boolean;
  planId?: number | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface GetTenantsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: TenantStatus;
}

// ---- Super Admin dashboard (master/control-plane analytics) ----
export interface PlatformStatSlice { label: string; count: number }
export interface PlatformGrowthPoint { period: string; newTenants: number; totalTenants: number }
export interface PlatformPlanStat { plan: string; tenants: number; mrr: number }
export interface PlatformRevenueSlice { label: string; value: number }
export interface PlatformActivity { action: string; module?: string | null; tenantId?: number | null; createdAt: string }

export interface PlatformDashboard {
  totalTenants: number; activeTenants: number; inactiveTenants: number; pendingTenants: number;
  suspendedTenants: number; cancelledTenants: number; trialTenants: number; expiredTenants: number;
  newTenantsThisMonth: number;
  totalPlans: number; activePlans: number;
  totalSubscriptions: number; activeSubscriptions: number; trialSubscriptions: number; pastDueSubscriptions: number;
  cancelledSubscriptions: number; expiredSubscriptions: number;
  mrr: number; arr: number; currency: string; totalPlatformUsers: number;
  tenantGrowth: PlatformGrowthPoint[];
  tenantsByStatus: PlatformStatSlice[];
  subscriptionsByStatus: PlatformStatSlice[];
  topPlans: PlatformPlanStat[];
  revenueByPlan: PlatformRevenueSlice[];
  recentActivities: PlatformActivity[];
}

// ---- Super admin tenant page (Module 0, O13) — mirrors PlatformTenantProfileDto ----

export interface TenantProfileRow {
  key: string;
  label: string;
  value: string;
}

export interface TrialExtension {
  id: number;
  days: number;
  reason: string;
  byName?: string | null;
  previousEndsAt?: string | null;
  newEndsAt: string;
  createdAt: string;
}

export type TrialStatus = 'Trial' | 'Ended' | 'Paid' | 'None';

export interface TenantTrial {
  startedAt?: string | null;
  endsAt?: string | null;
  daysLeft: number;
  status: TrialStatus;
  canExtend: boolean;
  extensions: TrialExtension[];
}

export interface SuggestedPlan {
  planId: number;
  name: string;
  maxUsers: number;
  neededUsers: number;
  fits: boolean;
  basis: string;
}

export interface PlatformTenantProfile {
  id: number;
  tenantNumber: string;
  companyName: string;
  legalName?: string | null;
  email: string;
  phone?: string | null;
  status: TenantStatus;
  signedUpAt: string;
  location?: string | null;
  ownerName?: string | null;
  ownerDesignation?: string | null;
  hasProfile: boolean;
  profile: TenantProfileRow[];
  notBuiltInterest: string[];
  trial: TenantTrial;
  suggestedPlan?: SuggestedPlan | null;
  /** NotStarted, Draft, Submitted, Approved or Rejected. */
  kycStatus: string;
}
